import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, Send, ShieldCheck, Target, Trash2, MessageSquare, AlertCircle, Sparkles, CheckCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SmartCoachButton, MetricSuggesterButton } from "@/components/AIAssistants";

const STATUS_STYLES = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  changes_requested: "bg-rose-50 text-rose-700 border-rose-200",
  approved: "bg-violet-50 text-violet-700 border-violet-200",
  locked: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Goals() {
  const { user } = useAuth();
  const [activeCycles, setActiveCycles] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const cycle = activeCycles[activeIdx]?.cycle;
  const participant = activeCycles[activeIdx]?.participant;
  const fw = cycle?.framework_snapshot;

  const reload = async () => {
    setLoading(true);
    const r = await api.get("/cycles/active/me");
    setActiveCycles(r.data.items || []);
    if (r.data.items?.[activeIdx]?.cycle) {
      const o = await api.get("/objectives", { params: { cycle_id: r.data.items[activeIdx].cycle.id } });
      setObjectives(o.data.items || []);
    } else {
      setObjectives([]);
    }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeIdx]);

  const totalWeight = objectives.reduce((s, o) => s + Number(o.weightage || 0), 0);
  const rules = fw?.weightage_rules || {};
  const canSubmit = useMemo(() => {
    if (!objectives.length) return false;
    const draftish = objectives.every((o) => ["draft", "changes_requested"].includes(o.status));
    if (!draftish) return false;
    if (rules.must_sum_to_100 && totalWeight !== 100) return false;
    if ((rules.min_objectives || 0) > objectives.length) return false;
    if ((rules.max_objectives || 999) < objectives.length) return false;
    return true;
  }, [objectives, rules, totalWeight]);

  const submitAll = async () => {
    try {
      const r = await api.post("/objectives/submit", { cycle_id: cycle.id });
      toast.success(`Submitted ${r.data.count} objectives to your reviewer`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  const remove = async (oid) => {
    try {
      await api.delete(`/objectives/${oid}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const sign = async (oid) => {
    try {
      await api.post(`/objectives/${oid}/sign`, { role: "employee" });
      toast.success("Signed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    }
  };

  if (!user?.employee_id) {
    return (
      <div className="max-w-2xl axia-card p-8" data-testid="goals-no-employee">
        <ShieldCheck className="w-6 h-6 text-amber-500" />
        <h2 className="font-display text-xl font-semibold mt-3 text-slate-900">No employee record linked</h2>
        <p className="text-sm text-slate-500 mt-2">Ask your HR admin to link your account to an employee record before setting goals.</p>
      </div>
    );
  }

  if (loading) return <div className="text-sm text-slate-500">Loading goals…</div>;

  if (activeCycles.length === 0) {
    return (
      <div className="max-w-2xl axia-card p-12 text-center" data-testid="goals-no-cycle">
        <Target className="w-7 h-7 mx-auto text-slate-300" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-3">No active cycle</h1>
        <p className="text-sm text-slate-500 mt-2">When HR activates a cycle that includes you, your objectives will appear here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6" data-testid="goals-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">My goals</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">{cycle.name}</h1>
          <p className="text-sm text-slate-500 mt-2">
            Framework: <span className="font-medium text-slate-800">{fw?.name}</span>
            {participant?.primary_reviewer_id && (
              <span> · Your reviewer is auto-assigned via your reporting chain.</span>
            )}
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-objective-btn"><Plus className="w-4 h-4 mr-2" /> Add objective</Button>
          </DialogTrigger>
          <ObjectiveDialog framework={fw} cycleId={cycle.id} onClose={() => { setCreateOpen(false); reload(); }} />
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat
          label="Objectives"
          value={objectives.length}
          hint={`${rules.min_objectives}–${rules.max_objectives} required`}
        />
        <Stat
          label="Weightage total"
          value={`${totalWeight}%`}
          hint={rules.must_sum_to_100 ? "Must equal 100" : "Free"}
          warn={rules.must_sum_to_100 && totalWeight !== 100}
        />
        <Stat
          label="Status"
          value={participant.status.replace(/_/g, " ")}
        />
        <Stat
          label="Cycle phase"
          value={(cycle.phases || []).find((p) => p.status === "open")?.label || "—"}
        />
      </div>

      {participant.status === "goals_drafting" && (
        <div className="axia-card p-4 flex items-center justify-between flex-wrap gap-3" data-testid="submit-bar">
          <div className="text-sm text-slate-600">
            {canSubmit
              ? "Looks good. Submit to your reviewer when ready."
              : "Hit the requirements above (count + weightage) to submit."}
          </div>
          <Button onClick={submitAll} disabled={!canSubmit} data-testid="submit-objectives-btn">
            <Send className="w-3.5 h-3.5 mr-2" /> Submit for review
          </Button>
        </div>
      )}

      {objectives.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <Target className="w-6 h-6 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No objectives yet</div>
          <p className="text-sm mt-1">Add your first objective above. Aim for {rules.min_objectives}–{rules.max_objectives}, summing to 100%.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {objectives.map((o) => (
            <ObjectiveCard
              key={o.id}
              objective={o}
              framework={fw}
              onChanged={reload}
              onDelete={() => remove(o.id)}
              onSign={() => sign(o.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, warn }) {
  return (
    <div className={`axia-card p-4 ${warn ? "border-amber-300" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="axia-overline">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${warn ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 capitalize">{hint}</div>}
    </div>
  );
}

function ObjectiveCard({ objective, framework, onChanged, onDelete, onSign }) {
  const [open, setOpen] = useState(false);
  const isLockedOrApproved = ["approved", "locked"].includes(objective.status);
  const fields = framework?.objective_fields || [];
  const empSigned = !!objective.sign_off?.employee_signed_at;

  return (
    <div className="axia-card p-5" data-testid={`objective-card-${objective.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`axia-pill ${STATUS_STYLES[objective.status]}`}>{(objective.status || "").replace(/_/g, " ")}</span>
            <span className="axia-overline text-slate-500">weight {objective.weightage}%</span>
            {objective.classification_value && (
              <span className="axia-pill bg-slate-50 text-slate-700 border-slate-200">{objective.classification_value}</span>
            )}
          </div>
          {fields.slice(0, 1).map((f) => (
            <div key={f.id} className="mt-3 font-display text-base font-semibold text-slate-900 leading-snug">
              {objective.fields?.[f.id] || <span className="text-slate-400">No {f.label.toLowerCase()}</span>}
            </div>
          ))}
          {fields.slice(1, 3).map((f) => (
            <div key={f.id} className="mt-2 text-sm text-slate-600">
              <span className="axia-overline mr-2">{f.label}</span>
              <span>{objective.fields?.[f.id] || "—"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {objective.sign_off?.employee_signed_at && (
            <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCheck className="w-3 h-3" /> Employee signed</span>
          )}
          {objective.sign_off?.manager_signed_at && (
            <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCheck className="w-3 h-3" /> Manager signed</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isLockedOrApproved} data-testid={`edit-objective-${objective.id}`}>Edit</Button>
            </DialogTrigger>
            <ObjectiveDialog
              objective={objective}
              framework={framework}
              cycleId={objective.cycle_id}
              onClose={() => { setOpen(false); onChanged?.(); }}
            />
          </Dialog>
          {objective.status === "approved" && !empSigned && (
            <Button size="sm" onClick={onSign} data-testid={`sign-objective-${objective.id}`}>
              <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Sign
            </Button>
          )}
          {!isLockedOrApproved && (
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-rose-600 hover:text-rose-700">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ObjectiveDialog({ objective, framework, cycleId, onClose }) {
  const isEdit = !!objective;
  const [fields, setFields] = useState(objective?.fields || {});
  const [classification, setClassification] = useState(objective?.classification_value || "");
  const [weightage, setWeightage] = useState(objective?.weightage ?? framework?.weightage_rules?.per_objective_min_weight ?? 10);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/objectives/${objective.id}`, { fields, classification_value: classification || null, weightage: Number(weightage) });
        toast.success("Updated");
      } else {
        await api.post("/objectives", {
          cycle_id: cycleId,
          fields,
          classification_value: classification || null,
          weightage: Number(weightage),
        });
        toast.success("Objective added");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl" data-testid="objective-dialog">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit objective" : "New objective"}</DialogTitle>
        <DialogDescription>{framework?.name}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {(framework?.objective_fields || []).map((f) => (
          <div key={f.id}>
            <Label>{f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}</Label>
            {f.help_text && <div className="text-[11px] text-slate-500 mb-1.5">{f.help_text}</div>}
            {f.type === "textarea" ? (
              <Textarea
                value={fields[f.id] || ""}
                onChange={(e) => setFields({ ...fields, [f.id]: e.target.value })}
                rows={3}
                data-testid={`field-${f.id}`}
              />
            ) : (
              <Input
                value={fields[f.id] || ""}
                onChange={(e) => setFields({ ...fields, [f.id]: e.target.value })}
                type={f.type === "number" ? "number" : "text"}
                data-testid={`field-${f.id}`}
              />
            )}
          </div>
        ))}
        {framework?.classification?.enabled && (
          <div>
            <Label>{framework.classification.dimension_name}</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger data-testid="classification-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {framework.classification.options.map((o) => (
                  <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Weightage (%)</Label>
          <Input type="number" min={0} max={100} value={weightage} onChange={(e) => setWeightage(e.target.value)} data-testid="weightage-input" />
          <div className="text-[11px] text-slate-500 mt-1">
            Min {framework?.weightage_rules?.per_objective_min_weight || 0}% per objective
          </div>
        </div>
      </div>
      <DialogFooter>
        <div className="flex-1 flex items-center gap-2">
          <SmartCoachButton
            cycleId={cycleId}
            fields={fields}
            framework={framework}
            onApply={(rewritten) => setFields((prev) => ({ ...prev, ...rewritten }))}
          />
          {(framework?.objective_fields || []).find((f) => /measure|metric|kpi/i.test(f.label)) && (
            <MetricSuggesterButton
              objectiveText={
                Object.values(fields).filter(Boolean).join(" · ") ||
                ((framework?.objective_fields || [])[0] && fields[(framework?.objective_fields || [])[0].id]) ||
                ""
              }
              onPick={(metricLine) => {
                const field = (framework?.objective_fields || []).find((f) => /measure|metric|kpi/i.test(f.label));
                if (field) setFields((prev) => ({ ...prev, [field.id]: prev[field.id] ? `${prev[field.id]}\n${metricLine}` : metricLine }));
              }}
            />
          )}
        </div>
        <Button onClick={submit} disabled={saving} data-testid="objective-save-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} {isEdit ? "Save changes" : "Add objective"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
